import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * A promise-based confirm dialog backed by Shadcn AlertDialog so every
 * destructive action in the app shares one consistent style (themed,
 * buttons on one line, keyboard-friendly, accessible) instead of the
 * browser's native window.confirm() which renders stacked OS buttons.
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Delete user?", danger: true }))) return;
 *
 * Wrap the app root with <ConfirmProvider/> (see AppRoot.jsx).
 */
const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    title: "",
    description: "",
    confirmLabel: "Confirm",
    cancelLabel: "Cancel",
    danger: false,
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        title: opts.title || "Are you sure?",
        description: opts.description || "",
        confirmLabel: opts.confirmLabel || (opts.danger ? "Delete" : "Confirm"),
        cancelLabel: opts.cancelLabel || "Cancel",
        danger: !!opts.danger,
      });
    });
  }, []);

  const close = (result) => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={state.open}
        onOpenChange={(v) => !v && close(false)}
      >
        <AlertDialogContent data-testid="global-confirm-dialog" className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{state.title}</AlertDialogTitle>
            {state.description && (
              <AlertDialogDescription className="pt-1 leading-relaxed">
                {state.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-row items-center justify-end gap-2 sm:gap-2">
            <AlertDialogCancel
              onClick={() => close(false)}
              data-testid="global-confirm-cancel"
              className="mt-0"
            >
              {state.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => close(true)}
              data-testid="global-confirm-ok"
              className={
                state.danger
                  ? "bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90"
                  : ""
              }
            >
              {state.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Graceful fallback so code paths that somehow render outside the
    // provider (tests, isolated stories) still work — default to the
    // native confirm() with no styling.
    return async (opts) =>
      window.confirm(
        [opts?.title, opts?.description].filter(Boolean).join("\n\n") ||
          "Are you sure?",
      );
  }
  return ctx;
}
